"use client";
import type { Stat, StatsBackground } from "@/components/Stats/types";
import { Children, ReactNode, isValidElement, type JSX } from "react";
import { usePathname } from "next/navigation";
import Stats from ".";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";

type ShortCodeStatsProps = {
  children?: ReactNode; // Changed from JSX.Element to ReactNode
  background?: StatsBackground;
  header?: string;
  subheader?: string;
  linkText?: string;
  linkUrl?: string;
  // Handle lowercase versions that come from HTML attribute parsing
  linktext?: string;
  linkurl?: string;
  variant?: "default" | "header-link";
  trackEvents?: boolean;
  htmlId?: string;
  location?: string;
  max_stats?: string;
};

const LEARN_PAGE_REGEX = /^\/learn\/[^/]+\/?(?:\?.*)?$/;

export function ShortCodeStats({
  children,
  background,
  header,
  subheader,
  linkText,
  linkUrl,
  linktext,
  linkurl,
  variant,
  trackEvents,
  htmlId,
  location,
  max_stats,
}: ShortCodeStatsProps) {
  const pathname = usePathname();
  const stats: Stat[] = [];
  const finalMaxStats = max_stats ? Number(max_stats) : undefined;

  Children.toArray(children).forEach((child: ReactNode, index: number) => {
    if (isValidElement(child)) {
      const element = child as React.ReactElement<{
        label: string;
        value?: string;
        icon?: string;
      }>;

      stats.push({
        label: element.props.label,
        value: element.props.value,
        icon: element.props.icon,
      });
    }
  });

  const finalLinkText = linkText || linktext;
  const finalLinkUrl = linkUrl || linkurl;

  if (location === "learn-page") {
    return (
      <FeatureFlagComponentResolverClient
        flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
        variants={{
          legacy: <> </>,
          test: (
            <MonarchResolver<LearnPageRedesignTestResult>
              experimentKey="useLearnPageRedesign"
              variants={{
                control: <> </>,
                test: (
                  <MonarchDataConsumer<LearnPageRedesignTestResult>
                    experimentKey="useLearnPageRedesign"
                    render={(testResult) => {
                      const isLearnPage = LEARN_PAGE_REGEX.test(pathname);
                      if (
                        (!testResult?.data?.stats ||
                          !testResult.data.stats?.enabled) &&
                        !isLearnPage
                      ) {
                        return null;
                      }
                      return (
                        <div className="py-0">
                          <Stats
                            stats={stats}
                            background={background}
                            header={header}
                            subheader={subheader}
                            linkText={finalLinkText}
                            linkUrl={finalLinkUrl}
                            variant={variant}
                            trackEvents={trackEvents}
                            htmlId={htmlId}
                            location={location}
                          />
                        </div>
                      );
                    }}
                  />
                ),
              }}
              Loader={null}
              fallbackVariant="control"
            />
          ),
        }}
        fallback="legacy"
      />
    );
  }

  return (
    <Stats
      stats={stats}
      background={background}
      header={header}
      subheader={subheader}
      linkText={finalLinkText}
      linkUrl={finalLinkUrl}
      variant={variant}
      trackEvents={trackEvents}
      maxStats={finalMaxStats}
    />
  );
}
